import { Components, Theme } from "@mui/material/styles";
import type {} from "@mui/lab/themeAugmentation";
import type {} from "@mui/x-data-grid/themeAugmentation";

import { ColorMode, StatusTone, tokens } from "./tokens";

type AppComponents = Components<Omit<Theme, "components">>;

/**
 * MUI component defaults and style overrides for one colour mode.
 * Everything is driven by the design tokens, so light and dark stay in step.
 */
export function buildComponents(mode: ColorMode): AppComponents {
  const t = tokens[mode];

  /** Alert as a callout: tone background, tone border, 4px left rule, tone icon/title. */
  const alertTone = (tone: StatusTone) => {
    const s = t.status[tone];
    return {
      backgroundColor: s.bg,
      color: t.text.primary,
      borderColor: s.line,
      borderLeftColor: s.fg,
      "& .MuiAlert-icon": { color: s.fg },
      "& .MuiAlertTitle-root": { color: s.fg },
    };
  };

  const headerCell = {
    fontSize: "0.6875rem",
    fontWeight: 500,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: t.text.secondary,
  };

  const floatingPaper = {
    borderRadius: 8,
    border: `1px solid ${t.border}`,
    boxShadow: "0px 10px 30px 0px rgba(0, 0, 0, 0.18)",
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: { colorScheme: mode },
        body: { backgroundColor: t.page },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          // Ripples are off, so keyboard focus needs its own visible ring.
          "&.Mui-focusVisible": {
            outline: `2px solid ${t.brand}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "hover",
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          minHeight: 34,
          fontWeight: 500,
          letterSpacing: "0.02em",
        },
        sizeSmall: {
          minHeight: 28,
        },
        sizeLarge: {
          minHeight: 40,
        },
        outlinedPrimary: {
          borderColor: t.brand,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${t.border}`,
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
      },
      styleOverrides: {
        action: {
          marginTop: "-4px",
          marginRight: "-4px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 24,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          backgroundImage: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.0625rem",
          fontWeight: 500,
          lineHeight: 1.3,
          padding: "20px 22px 12px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "4px 22px",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontSize: "0.84375rem",
          lineHeight: 1.5,
          color: t.text.secondary,
        },
      },
    },
    MuiDialogActions: {
      defaultProps: {
        disableSpacing: true,
      },
      styleOverrides: {
        root: {
          gap: 8,
          flexWrap: "wrap",
          padding: "16px 22px 18px",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: floatingPaper,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: floatingPaper,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": { outlineOffset: -2 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": { outlineOffset: -2 },
          "&.Mui-selected": {
            backgroundColor: t.brandTint,
            color: t.brandDark,
            "& .MuiListItemIcon-root": { color: "inherit" },
            "&:hover, &.Mui-focusVisible": { backgroundColor: t.brandTint },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.grey200}`,
        },
        head: {
          ...headerCell,
          backgroundColor: t.grey50,
          borderBottom: `1px solid ${t.border}`,
          whiteSpace: "nowrap",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover:hover": { backgroundColor: t.grey50 },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: t.brandTint,
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: t.border,
        },
        withBorderColor: {
          borderColor: t.border,
        },
        columnHeaders: {
          backgroundColor: t.grey50,
          borderBottom: `1px solid ${t.border}`,
        },
        columnHeaderTitle: headerCell,
        cell: {
          borderBottomColor: t.grey200,
        },
        row: {
          "&:hover, &.Mui-hovered": { backgroundColor: t.grey50 },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: "1px solid",
          borderLeftWidth: 4,
          padding: "8px 14px",
          fontSize: "0.84375rem",
        },
        standardSuccess: alertTone("ok"),
        standardInfo: alertTone("info"),
        standardWarning: alertTone("warn"),
        standardError: alertTone("err"),
        outlinedSuccess: alertTone("ok"),
        outlinedInfo: alertTone("info"),
        outlinedWarning: alertTone("warn"),
        outlinedError: alertTone("err"),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: 500,
          marginTop: 0,
          marginBottom: 2,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
  };
}

export default buildComponents;
